import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function RouteCollage() {
  const { t } = useTranslation();
  const ROUTE_IMAGES = [
    {
      src: "/route-shikoku.jpg",
      alt: t('routes_badges.routes.0.name'),
      tag: t('routes_badges.routes.0.tag'),
      color: "#F59E0B",
      rotate: -8,
      x: -15,
      y: 10,
      z: 1,
    },
    {
      src: "/route-tokaido.jpg",
      alt: t('routes_badges.routes.1.name'),
      tag: t('routes_badges.routes.1.tag'),
      color: "#10B981",
      rotate: -3,
      x: 5,
      y: -5,
      z: 2,
    },
    {
      src: "/route-europe.jpg",
      alt: t('routes_badges.routes.2.name'),
      tag: t('routes_badges.routes.2.tag'),
      color: "#8B5CF6",
      rotate: 4,
      x: 15,
      y: 5,
      z: 3,
    },
    {
      src: "/route-hokkaido.jpg",
      alt: t('routes_badges.routes.3.name'),
      tag: t('routes_badges.routes.3.tag'),
      color: "#13B6EC",
      rotate: 10,
      x: -5,
      y: -8,
      z: 4,
    },
  ];

  const tilts = [-3, 2, 2, -2];
  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-sky-50 via-white to-blue-50 p-3">
      <div className="grid grid-cols-2 gap-1.5 w-full h-full">
        {ROUTE_IMAGES.map((img, idx) => (
          <div
            key={img.alt}
            className="relative rounded-lg overflow-hidden shadow-md"
            style={{
              transform: `rotate(${tilts[idx]}deg)`,
              border: "2px solid white",
            }}
          >
            <img
              src={img.src}
              alt={img.alt}
              className="w-full h-full object-cover"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            {/* Tag badge */}
            <div
              className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-white font-bold"
              style={{ fontSize: "6px", background: img.color }}
            >
              {img.tag}
            </div>
            {/* Route name */}
            <div className="absolute bottom-1 left-1 right-1">
              <p className="text-white font-black leading-tight" style={{ fontSize: "7px", fontFamily: "'Noto Sans JP', sans-serif", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                {img.alt}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  const { t } = useTranslation();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const FEATURES = [
    {
      icon: "ri-map-2-fill",
      title: t('features.list.walk.title'),
      subtitle: t('features.list.walk.subtitle'),
      desc: t('features.list.walk.desc'),
      accent: t('features.list.walk.accent'),
      color: "#13B6EC",
      img: "/map-screen.png",
      type: "standard" as const,
    },
    {
      icon: "ri-route-fill",
      title: t('features.list.routes.title'),
      subtitle: t('features.list.routes.subtitle'),
      desc: t('features.list.routes.desc'),
      accent: t('features.list.routes.accent'),
      color: "#0E8FBD",
      img: "",
      type: "collage" as const,
    },
    {
      icon: "ri-quill-pen-fill",
      title: t('features.list.diary.title'),
      subtitle: t('features.list.diary.subtitle'),
      desc: t('features.list.diary.desc'),
      accent: t('features.list.diary.accent'),
      color: "#5FCEEF",
      img: "/diary-screen.png",
      type: "diary" as const,
    },
  ];

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getStyle = (idx: number): React.CSSProperties => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.97)",
    transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
    transitionDelay: `${idx * 120}ms`,
  });

  return (
    <section id="features" ref={sectionRef} className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold text-[#13B6EC] bg-[#13B6EC]/8 tracking-widest uppercase mb-5">
            {t('features.label')}
          </div>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {t('features.title')}
          </h2>
          <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            {t('features.desc')}
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {FEATURES.map((feat, idx) => (
            <div
              key={feat.title}
              style={getStyle(idx)}
              className="group rounded-3xl overflow-hidden bg-white border border-gray-100 hover:border-transparent transition-all duration-400 hover:-translate-y-2"
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = `0 20px 60px ${feat.color}22`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = "";
              }}
            >
              {/* Image area */}
              <div className="relative overflow-hidden" style={{ height: "200px" }}>
                {feat.type === "collage" ? (
                  <RouteCollage />
                ) : feat.type === "diary" ? (
                  <div className="relative w-full h-full overflow-hidden">
                    {/* Background atmospheric photo */}
                    <img
                      src="/diary-bg.jpg"
                      alt={feat.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Dark overlay for text readability */}
                    <div className="absolute inset-0 bg-black/55" />
                    {/* Diary text overlay */}
                    <div className="absolute inset-0 flex flex-col justify-center px-5 py-4 overflow-hidden">
                      <p
                        className="text-white/90 font-bold text-xs mb-2"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {t('features.diary_screenshot.day_title')}
                      </p>
                      <p
                        className="text-white/70 leading-relaxed"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "9px", lineHeight: "1.8" }}
                      >
                        {t('features.diary_screenshot.text').split('\n').map((line, i) => <span key={i}>{line}<br /></span>)}
                      </p>
                    </div>
                    {/* Bottom fade */}
                    <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                ) : (
                  <img
                    src={feat.img}
                    alt={feat.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-108"
                    style={{ objectFit: "cover" }}
                  />
                )}
                {feat.type === "standard" && (
                  <div
                    className="absolute inset-0 opacity-50"
                    style={{ background: `linear-gradient(to bottom, transparent 40%, ${feat.color}60)` }}
                  />
                )}
                {/* Accent badge */}
                <div
                  className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold text-white"
                  style={{ background: feat.color }}
                >
                  {feat.accent}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-xs font-semibold mb-1 tracking-wider uppercase" style={{ color: feat.color }}>
                  {feat.subtitle}
                </p>
                <h3
                  className="text-xl font-black text-gray-900 mb-3"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {feat.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  {feat.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
